/**
 * Kit de controles equivalente aos `TRExt*` do cliente de referência.
 * Cada variante corresponde a um StyleLookup (docs/spec/theme.md).
 */
import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from './icons.js';

export type ButtonVariant =
  | 'action' // actionButtonStyle
  | 'smallAction' // smallFontActionButtonStyle
  | 'secondary' // secondaryActionButtonStyle
  | 'link' // linkButtonStyle
  | 'strongLink' // strongLinkButtonStyle
  | 'linkDropdown' // linkDropDownButtonStyle
  | 'linkAction' // linkActionButtonStyle
  | 'ghost'; // sem fundo nem contorno: ações de apoio numa barra

/** Os três tamanhos do padrão: pequeno (painéis da mesa), médio (o padrão) e grande (celular, destaque). */
export type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Mostra o indicador de atividade (TAniIndicator) por cima do rótulo. */
  busy?: boolean;
  icon?: IconName;
};

export function Button({ variant = 'secondary', size = 'md', busy = false, icon, className, children, disabled, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      className={`rd-btn rd-btn--${variant}${size === 'md' ? '' : ` rd-btn--${size}`}${className ? ` ${className}` : ''}`}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {icon ? <Icon name={icon} size={16} /> : null}
      {children}
      {variant === 'linkDropdown' ? <span className="rd-btn__caret" aria-hidden="true" /> : null}
      {busy ? (
        <span className="rd-btn__busy">
          <span className="rd-spinner" />
        </span>
      ) : null}
    </button>
  );
}

export function Spinner({ size = 18 }: { size?: number }) {
  return <span className="rd-spinner" style={{ width: size, height: size }} role="status" />;
}

type EditProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
  icon?: IconName;
  /** 'credential' = logineditstyle/passwordeditstyle; 'filled' = edit comum de formulário */
  look?: 'credential' | 'filled';
  invalid?: boolean;
  autoFocusOnMount?: boolean;
  /** Num campo de senha, o olho à direita que mostra e esconde o que foi digitado (os dois rótulos do botão). */
  reveal?: { show: string; hide: string };
};

export function TextEdit({ value, onChange, icon, look = 'credential', invalid, autoFocusOnMount, reveal, className, style, type, ...rest }: EditProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (autoFocusOnMount) ref.current?.focus();
  }, [autoFocusOnMount]);
  const cls = [
    'rd-edit',
    look === 'filled' ? 'rd-edit--filled' : '',
    icon ? 'rd-edit--withIcon' : '',
    reveal ? 'rd-edit--withReveal' : '',
    invalid ? 'rd-edit--error' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls} style={style}>
      {icon ? (
        <span className="rd-edit__icon" aria-hidden="true">
          <Icon name={icon} size={18} />
        </span>
      ) : null}
      <input
        ref={ref}
        className="rd-edit__input"
        value={value}
        type={reveal && shown ? 'text' : type}
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
      {reveal ? (
        <button
          type="button"
          className="rd-iconBtn rd-edit__reveal"
          aria-label={shown ? reveal.hide : reveal.show}
          aria-pressed={shown}
          title={shown ? reveal.hide : reveal.show}
          onClick={() => setShown((v) => !v)}
        >
          <Icon name={shown ? 'eyeOff' : 'eye'} size={18} />
        </button>
      ) : null}
    </div>
  );
}

type ComboProps<T extends string> = {
  value: T | '';
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  placeholder?: string;
  invalid?: boolean;
  style?: React.CSSProperties;
  'aria-label'?: string;
  /** para um `<label htmlFor>` de fora */
  id?: string;
  'aria-describedby'?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLSelectElement>;
};

export function ComboBox<T extends string>({ value, onChange, options, placeholder, invalid, style, ...rest }: ComboProps<T>) {
  return (
    <div className={`rd-combo${invalid ? ' rd-combo--error' : ''}`} style={style}>
      <select
        className="rd-combo__select"
        value={value}
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange(e.target.value as T)}
        {...rest}
      >
        {placeholder !== undefined ? (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        ) : null}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span className="rd-combo__arrow">
        <Icon name="chevronDown" size={14} />
      </span>
    </div>
  );
}

type CheckProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  /** vtopcheckboxstyle: caixa alinhada ao topo do texto (textos de várias linhas) */
  alignTop?: boolean;
  style?: React.CSSProperties;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
};

export function CheckBox({ checked, onChange, children, alignTop, style, onKeyDown }: CheckProps) {
  return (
    <label className={`rd-check${alignTop ? ' rd-check--top' : ''}`} style={style}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} onKeyDown={onKeyDown} />
      <span className="rd-check__box" aria-hidden="true">
        {checked ? <Icon name="check" size={12} /> : null}
      </span>
      <span>{children}</span>
    </label>
  );
}

/* ------------------------------------------------------------------ chave, segmentado e deslizante */

/** O rótulo de fora: `<label htmlFor={id}>`, `aria-labelledby` (o segmentado não é rotulável) ou `aria-label`. */
type Labelled = { id?: string; 'aria-label'?: string; 'aria-labelledby'?: string; 'aria-describedby'?: string };

/** Chave de liga e desliga: um botão com `role="switch"`. Clicar no `<label htmlFor>` também alterna. */
export function Switch({ checked, onChange, disabled, ...label }: Labelled & { checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} className={`rd-switch${checked ? ' is-on' : ''}`} onClick={() => onChange(!checked)} {...label}>
      <span className="rd-switch__knob" aria-hidden="true" />
    </button>
  );
}

/**
 * Escolha de uma opção entre poucas, lado a lado (o "controle segmentado"): um `radiogroup` em que as
 * setas andam entre as opções e o Tab entra só na escolhida.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  ...label
}: Labelled & { value: T; onChange: (value: T) => void; options: readonly { value: T; label: string }[] }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = Math.max(0, options.findIndex((o) => o.value === value));
  const move = (index: number) => {
    const i = (index + options.length) % options.length;
    onChange(options[i]!.value);
    refs.current[i]?.focus();
  };
  return (
    <div role="radiogroup" className="rd-seg" {...label}>
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          tabIndex={i === current ? 0 : -1}
          className={`rd-seg__opt${o.value === value ? ' is-on' : ''}`}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') move(i + 1);
            else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') move(i - 1);
            else return;
            e.preventDefault();
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Valor numa faixa, com o valor escrito ao lado (`format`: "14 px", "80%"). */
export function Slider({
  value,
  onChange,
  min,
  max,
  step = 1,
  format,
  disabled,
  ...label
}: Labelled & { value: number; onChange: (value: number) => void; min: number; max: number; step?: number; format: (value: number) => string; disabled?: boolean }) {
  return (
    <span className="rd-slider">
      <input
        className="rd-range rd-slider__input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-valuetext={format(value)}
        onChange={(e) => onChange(Number(e.target.value))}
        {...label}
      />
      <output className="rd-slider__out" htmlFor={label.id}>
        {format(value)}
      </output>
    </span>
  );
}

export function ErrorLabel({ children, style }: { children?: ReactNode; style?: React.CSSProperties }) {
  return (
    <div className="rd-label rd-label--error" role={children ? 'alert' : undefined} style={style}>
      {children}
    </div>
  );
}

type DialogProps = {
  title: string;
  children: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  wide?: boolean;
  /** sem o título nem o espaço em volta: o conteúdo monta o próprio layout (o título vira só o nome acessível) */
  bare?: boolean;
  className?: string;
};

const openDialogs: string[] = [];

/** Diálogo modal simples (FCRep.BasePopup / messageDialog). Esc fecha o de cima. */
export function Dialog({ title, children, onClose, actions, wide, bare, className }: DialogProps) {
  const titleId = useId();
  useEffect(() => {
    // só o diálogo de cima fecha com Esc (uma pergunta pode abrir sobre outro diálogo)
    openDialogs.push(titleId);
    return () => {
      openDialogs.splice(openDialogs.lastIndexOf(titleId), 1);
    };
  }, [titleId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openDialogs.at(-1) === titleId) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, titleId]);
  const classes = ['rd-dialog', wide ? 'rd-dialog--wide' : '', bare ? 'rd-dialog--bare' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <div className="rd-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      {bare ? (
        <div className={classes} role="dialog" aria-modal="true" aria-label={title}>
          {children}
        </div>
      ) : (
        <div className={classes} role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <h2 className="rd-dialog__title" id={titleId}>
            {title}
          </h2>
          <div className="rd-dialog__body">{children}</div>
          {actions ? <div className="rd-dialog__actions">{actions}</div> : null}
        </div>
      )}
    </div>
  );
}
